/**
 * Authenticated API client.
 *
 * Wraps fetch with:
 *   - automatic `Authorization: Bearer <token>` from MSAL silent acquisition
 *   - automatic 401 retry (re-acquires token then retries once)
 *   - JSON parse and typed return
 *   - throws on non-2xx with a usable error
 */

import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { msalInstance, apiTokenRequest } from "./msal";

/**
 * Acquire an access token for the API.
 *
 * Silent first; if MSAL says we need interaction (refresh-token revoked,
 * consent change, conditional-access challenge), trigger a redirect so the
 * user logs in fresh instead of falling into a stuck error state. This is
 * the "they were signed in yesterday but admin changed group membership
 * today" recovery path.
 */
async function getAccessToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiTokenRequest, account });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Will redirect away; this Promise effectively never resolves.
      await msalInstance.acquireTokenRedirect({ ...apiTokenRequest, account });
      return null;
    }
    // Some other silent failure (network, etc.) — let the caller see 401.
    return null;
  }
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API error ${status}`);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* ignore */ }
    throw new ApiError(res.status, body);
  }
  return res.json() as Promise<T>;
}
